import React from 'react';
import { HelpTooltip } from './HelpTooltip';
import { wt } from './widgetTokens';

interface SeedWidgetProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  description?: string;
  helpText?: string;
}

export const SeedWidget: React.FC<SeedWidgetProps> = ({
  label,
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  description,
  helpText
}) => {
  // -1 means random mode (server will generate random seed on each execution)
  const isRandomMode = value === -1;

  const toggleMode = () => {
    if (isRandomMode) {
      // Switch to fixed mode with a random seed
      const randomSeed = Math.floor(Math.random() * 999999999999);
      onChange(randomSeed);
    } else {
      // Switch to random mode
      onChange(-1);
    }
  };

  const generateNewRandomSeed = () => {
    const randomSeed = Math.floor(Math.random() * 999999999999);
    onChange(randomSeed);
  };

  const activeBtnCls = 'bg-blue-500 text-white border-blue-500';
  const inactiveBtnCls = 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50';
  const baseBtnCls = 'flex-1 py-2 px-3 text-label font-medium border transition-colors duration-fast first:rounded-l-md last:rounded-r-md';

  return (
    <div className={wt.container}>
      <div className={wt.labelRow}>
        <label className={wt.label}>
          {label}
          {required && <span className={wt.required}>*</span>}
        </label>
        {helpText && <HelpTooltip helpText={helpText} />}
      </div>
      {description && <p className={wt.description}>{description}</p>}

      {/* Random / Fixed toggle */}
      <div className="flex rounded-md overflow-hidden border border-gray-300 w-fit">
        <button type="button" onClick={toggleMode} disabled={disabled}
          className={`${baseBtnCls} ${isRandomMode ? activeBtnCls : inactiveBtnCls}`}
          title="Random seed on each generation">
          🎲 Random
        </button>
        <button type="button" onClick={toggleMode} disabled={disabled}
          className={`${baseBtnCls} border-l ${!isRandomMode ? activeBtnCls : inactiveBtnCls}`}
          title="Use a fixed seed value">
          🔒 Fixed
        </button>
      </div>

      {/* Fixed seed input */}
      {!isRandomMode && (
        <div className={wt.seedWrap}>
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(Number(e.target.value))}
            disabled={disabled}
            placeholder="Enter seed value"
            aria-label={label}
            className={`${wt.seedInput} ${error ? wt.inputError : ''}`}
          />
          <button type="button" onClick={generateNewRandomSeed} disabled={disabled}
            title="Generate a new random seed" className={wt.seedBtn}>
            🎲
          </button>
        </div>
      )}

      {/* Random mode indicator */}
      {isRandomMode && (
        <p className="text-meta text-gray-500 italic">
          A new random seed will be used for each generation.
        </p>
      )}

      {error && <p className={wt.errorMsg}>{error}</p>}
    </div>
  );
};

export default SeedWidget;
